import { PageHeader } from "@/components/PageHeader";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useSession } from "@/lib/auth-client";
import { useOrgRole } from "@/lib/use-org-role";
import { HubApps } from "@/pages/container-tabs/integrations/HubApps";
import { OrgConnectionPage, OrgConnectionsTab } from "@/pages/org-connections/OrgConnections";
import { ConnectorSetupSection } from "@/pages/org-integrations/ConnectorSetupSection";
import { OrgModelsTab } from "@/pages/org-models/OrgModelsTab";
import type { ReactNode } from "react";

// Shell for the org's top-level sidebar pages (Settings, Connections,
// Integrations, Models). Every one opens with a title and a subtitle: one plain
// sentence saying what the page is for, in an exec's words ("organization",
// not "workspace"; no plumbing). The page body starts straight after it, with
// no second intro paragraph.
export function OrgPage({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  useDocumentTitle(title);
  const { data: session } = useSession();

  if (!session?.session.activeOrganizationId) {
    return <div className="p-6 text-sm text-muted-foreground">No active organization.</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader title={title} subtitle={subtitle} />
      {children}
    </div>
  );
}

export function OrgConnectionsPage() {
  return (
    <OrgPage
      title="Connections"
      subtitle="Where your team chats with Boos (Slack, Teams) and where your code lives (GitHub, GitLab)."
    >
      <OrgConnectionsTab />
    </OrgPage>
  );
}

// One shared connection. It names itself and links back to Connections, so no
// page heading above it.
export function OrgConnectionDetailPage() {
  useDocumentTitle("Connections");
  return (
    <div className="p-6">
      <OrgConnectionPage />
    </div>
  );
}

// The work apps, then who may fill in the connections custom integrations ask for.
export function OrgIntegrationsPage() {
  const { orgId } = useOrgRole();
  return (
    <OrgPage title="Integrations" subtitle="Work apps your Boos can use, like Notion, HubSpot and Salesforce.">
      <div className="flex flex-col gap-10">
        <HubApps />
        {orgId && <ConnectorSetupSection organizationId={orgId} />}
      </div>
    </OrgPage>
  );
}

export function OrgModelsPage() {
  return (
    <OrgPage title="Models" subtitle="Choose the default models for your Boos, which each Boo's admins can override.">
      <OrgModelsTab />
    </OrgPage>
  );
}
